import { useState, useEffect, useContext } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { AppContext } from '@/App';
import axios from 'axios';
import { Coins, ArrowUpCircle, ArrowDownCircle, RefreshCw, CheckCircle, XCircle, Activity } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function BlockchainWallet() {
  const { user } = useContext(AppContext);
  const [blockchainInfo, setBlockchainInfo] = useState(null);
  const [blockchainStatus, setBlockchainStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [withdrawAddress, setWithdrawAddress] = useState('');
  const [transactions, setTransactions] = useState([]);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Get blockchain status
      const statusRes = await axios.get(`${API}/blockchain/status`);
      setBlockchainStatus(statusRes.data);
      
      // Get user blockchain info
      const userRes = await axios.get(`${API}/blockchain/user/${user.id}`);
      setBlockchainInfo(userRes.data);
      
      // Get transactions
      const txRes = await axios.get(`${API}/blockchain/transactions/${user.id}`);
      setTransactions(txRes.data);
      
    } catch (error) {
      console.error('Error fetching blockchain data:', error);
      toast.error('Failed to load blockchain data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) {
      fetchData();
    }
  }, [user]);

  const handleSync = async () => {
    try {
      setSyncing(true);
      const response = await axios.post(`${API}/blockchain/sync-balance/${user.id}`);
      toast.success('Balance synced successfully');
      setBlockchainInfo(prev => ({
        ...prev,
        blockchain_balance: response.data.blockchain_balance
      }));
    } catch (error) {
      toast.error('Sync failed: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSyncing(false);
    }
  };

  const handleWithdraw = async () => {
    if (!withdrawAmount || parseFloat(withdrawAmount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    try {
      const response = await axios.post(`${API}/blockchain/withdraw`, null, {
        params: {
          user_id: user.id,
          amount: parseFloat(withdrawAmount),
          to_address: withdrawAddress || undefined
        }
      });
      
      toast.success(`Withdrawal successful! TX ID: ${response.data.txid.substring(0, 16)}...`);
      setWithdrawAmount('');
      setWithdrawAddress('');
      fetchData();
    } catch (error) {
      toast.error('Withdrawal failed: ' + (error.response?.data?.detail || error.message));
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto p-6">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">TIME Coin Blockchain Wallet</h1>
        <p className="text-gray-600">Manage your real blockchain-based TIME tokens</p>
      </div>

      {/* Blockchain Status */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="w-5 h-5" />
            Blockchain Status
          </CardTitle>
        </CardHeader>
        <CardContent>
          {blockchainStatus && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <p className="text-sm text-gray-600">Status</p>
                <div className="flex items-center gap-2 mt-1">
                  {blockchainStatus.status === 'connected' ? (
                    <>
                      <CheckCircle className="w-4 h-4 text-green-600" />
                      <span className="font-semibold text-green-600">Connected</span>
                    </>
                  ) : (
                    <>
                      <XCircle className="w-4 h-4 text-red-600" />
                      <span className="font-semibold text-red-600">Disconnected</span>
                    </>
                  )}
                </div>
              </div>
              <div>
                <p className="text-sm text-gray-600">Network</p>
                <p className="font-semibold mt-1">{blockchainStatus.network || 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Block Height</p>
                <p className="font-semibold mt-1">{blockchainStatus.block_height?.toLocaleString() || 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Peers</p>
                <p className="font-semibold mt-1">{blockchainStatus.connections || 0}</p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Wallet Info */}
      <div className="grid md:grid-cols-2 gap-6 mb-6">
        <Card>
          <CardHeader>
            <CardTitle>Your Blockchain Wallet</CardTitle>
            <CardDescription>Real TIME Coin blockchain address</CardDescription>
          </CardHeader>
          <CardContent>
            {blockchainInfo?.has_address ? (
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-gray-600 mb-2">Address</p>
                  <div className="bg-gray-100 p-3 rounded-lg font-mono text-sm break-all">
                    {blockchainInfo.address}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">Blockchain Balance</p>
                    <p className="text-2xl font-bold text-purple-600 mt-1">
                      {blockchainInfo.blockchain_balance?.toFixed(2)} TIME
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">App Balance</p>
                    <p className="text-2xl font-bold text-blue-600 mt-1">
                      {blockchainInfo.app_balance?.toFixed(2)} TIME
                    </p>
                  </div>
                </div>
                <Button 
                  onClick={handleSync} 
                  disabled={syncing}
                  className="w-full"
                  variant="outline"
                >
                  <RefreshCw className={`w-4 h-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
                  Sync Balance
                </Button>
              </div>
            ) : (
              <div className="text-center py-8">
                <Coins className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">No blockchain address assigned</p>
                <p className="text-sm text-gray-500 mt-2">Register a new account to get one</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Withdraw */}
        <Card>
          <CardHeader>
            <CardTitle>Withdraw to Blockchain</CardTitle>
            <CardDescription>Move tokens from app to blockchain</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">
                  Amount (TIME)
                </label>
                <Input
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                  value={withdrawAmount}
                  onChange={(e) => setWithdrawAmount(e.target.value)}
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">
                  Recipient Address (optional)
                </label>
                <Input
                  type="text"
                  placeholder="Leave empty to send to your own address"
                  value={withdrawAddress}
                  onChange={(e) => setWithdrawAddress(e.target.value)}
                />
              </div>
              <Button 
                onClick={handleWithdraw}
                className="w-full bg-purple-600 hover:bg-purple-700"
                disabled={!blockchainInfo?.has_address}
              >
                <ArrowUpCircle className="w-4 h-4 mr-2" />
                Withdraw to Blockchain
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Transactions */}
      <Card>
        <CardHeader>
          <CardTitle>Transaction History</CardTitle>
          <CardDescription>Recent blockchain transactions</CardDescription>
        </CardHeader>
        <CardContent>
          {transactions?.app_transactions?.length > 0 || transactions?.blockchain_transactions?.length > 0 ? (
            <div className="space-y-4">
              {transactions.app_transactions?.map((tx) => (
                <div key={tx.id} className="flex items-center justify-between p-4 border rounded-lg">
                  <div className="flex items-center gap-3">
                    <ArrowUpCircle className="w-5 h-5 text-purple-600" />
                    <div>
                      <p className="font-semibold">{tx.type.replace(/_/g, ' ').toUpperCase()}</p>
                      <p className="text-sm text-gray-500">
                        {new Date(tx.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold">{tx.amount} TIME</p>
                    {tx.txid && (
                      <p className="text-xs text-gray-500">{tx.txid.substring(0, 16)}...</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              No transactions yet
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default BlockchainWallet;
